/**
 * YouTube Player Component
 *
 * Embeds YouTube IFrame Player API for video playback
 * Provides play/pause, seek, volume controls
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import CastButton from './CastButton';
import './YouTubePlayer.css';

// Global flag to track if YouTube IFrame API is loaded
let isYouTubeAPILoaded = false;
let youtubeAPILoadPromise = null;

function YouTubePlayer({
  videoId,
  isPlaying,
  onPlay,
  onPause,
  onEnded,
  onNext,
  onPrevious,
  currentTrack,
  queueLength = 0,
  currentIndex = 0,
  syncTime = null, // Synchronized time from Firestore (for participants)
  onTimeUpdate = null, // Callback to update Firestore with current time (for host)
  isHost = false, // Whether this user is the host
}) {
  const playerRef = useRef(null);
  const containerRef = useRef(null);
  const [player, setPlayer] = useState(null);
  const [playerReady, setPlayerReady] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(100);
  const [isMuted, setIsMuted] = useState(false);
  const lastSyncTimeRef = useRef(null); // Track last synced time to avoid unnecessary seeks
  const isSeekingRef = useRef(false); // Flag to prevent sync loops during user seeks
  const hasSyncedForTrackRef = useRef(false); // Track if we've synced for current track
  const lastVideoIdRef = useRef(null); // Track video ID to detect track changes
  const prevIsPlayingRef = useRef(false); // Track previous playing state
  const prevSyncTimeRef = useRef(null); // Track previous sync time to detect jumps

  // Load YouTube IFrame API
  const loadYouTubeAPI = useCallback(() => {
    if (isYouTubeAPILoaded) {
      return Promise.resolve();
    }

    if (youtubeAPILoadPromise) {
      return youtubeAPILoadPromise;
    }

    youtubeAPILoadPromise = new Promise((resolve) => {
      // If already loaded
      if (window.YT && window.YT.Player) {
        isYouTubeAPILoaded = true;
        resolve();
        return;
      }

      // Create callback for when API is ready
      window.onYouTubeIframeAPIReady = () => {
        console.log('✅ YouTube IFrame API ready');
        isYouTubeAPILoaded = true;
        resolve();
      };

      // Load the script
      const script = document.createElement('script');
      script.src = 'https://www.youtube.com/iframe_api';
      script.async = true;
      document.body.appendChild(script);
    });

    return youtubeAPILoadPromise;
  }, []);

  // Initialize player when videoId changes
  useEffect(() => {
    if (!videoId) return;

    let isMounted = true;

    const initPlayer = async () => {
      await loadYouTubeAPI();

      if (!isMounted || !containerRef.current) return;

      // Destroy existing player
      if (playerRef.current) {
        playerRef.current.destroy();
        playerRef.current = null;
      }

      // Create new player
      playerRef.current = new window.YT.Player(containerRef.current, {
        videoId: videoId,
        height: '100%',
        width: '100%',
        playerVars: {
          autoplay: isPlaying ? 1 : 0,
          controls: 0, // Hide default controls, we use our own
          modestbranding: 1,
          rel: 0, // Don't show related videos
          showinfo: 0,
          fs: 0, // Disable fullscreen button
          playsinline: 1,
          enablejsapi: 1, // Enable JavaScript API
          origin: window.location.origin, // Set origin to allow embedding
          iv_load_policy: 3, // Hide annotations
        },
        events: {
          onReady: (event) => {
            if (!isMounted) return;
            console.log('✅ YouTube Player ready');
            setPlayer(event.target);
            setPlayerReady(true);
            setDuration(event.target.getDuration());

            // Set initial volume
            event.target.setVolume(volume);

            if (isPlaying) {
              event.target.playVideo();
            }
          },
          onStateChange: (event) => {
            if (!isMounted) return;

            // YT.PlayerState: ENDED=0, PLAYING=1, PAUSED=2, BUFFERING=3, CUED=5
            switch (event.data) {
              case 0: // ENDED
                onEnded && onEnded();
                break;
              case 1: // PLAYING
                onPlay && onPlay();
                break;
              case 2: // PAUSED
                onPause && onPause();
                break;
              default:
                break;
            }
          },
          onError: (event) => {
            console.error('YouTube Player error:', event.data);
          },
        },
      });
    };

    initPlayer();

    return () => {
      isMounted = false;
    };
  }, [videoId, loadYouTubeAPI]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle play/pause changes
  useEffect(() => {
    if (!player || !playerReady) return;

    if (isPlaying) {
      player.playVideo();
    } else {
      player.pauseVideo();
    }
  }, [isPlaying, player, playerReady]);

  // Update current time periodically and sync
  useEffect(() => {
    if (!player || !playerReady) return;

    const interval = setInterval(() => {
      if (player.getCurrentTime) {
        const time = player.getCurrentTime();
        setCurrentTime(time);

        // Host broadcasts time to Firestore
        if (isHost && onTimeUpdate && isPlaying && !isSeekingRef.current) {
          onTimeUpdate(time);
        }
      }
      if (player.getDuration && duration === 0) {
        setDuration(player.getDuration());
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [player, playerReady, duration, isHost, onTimeUpdate, isPlaying]);

  // Reset sync flag when video changes
  useEffect(() => {
    if (videoId !== lastVideoIdRef.current) {
      hasSyncedForTrackRef.current = false;
      lastVideoIdRef.current = videoId;
      lastSyncTimeRef.current = null;
      prevSyncTimeRef.current = null;
      prevIsPlayingRef.current = false;
    }
  }, [videoId]);

  // Sync to Firestore time - only when playback starts or host seeks significantly
  useEffect(() => {
    if (!player || !playerReady || !syncTime || syncTime === null) {
      prevSyncTimeRef.current = syncTime;
      return;
    }

    if (isSeekingRef.current) {
      prevSyncTimeRef.current = syncTime;
      return; // Don't sync during manual seeks
    }

    // Detect if playback just started (transition from paused to playing)
    const playbackJustStarted = isPlaying && !prevIsPlayingRef.current;

    // Detect if host seeked (significant jump in syncTime, not just incremental)
    const syncTimeJump =
      prevSyncTimeRef.current !== null &&
      Math.abs(syncTime - prevSyncTimeRef.current) > 2;

    // Sync conditions:
    // 1. Playback just started:
    //    - For non-host: always sync to host's current time when they hit play
    //    - For host: only sync if we haven't synced for this track yet
    // 2. Host made a significant seek (jump > 2 seconds)
    const shouldSync =
      (playbackJustStarted && (!isHost || !hasSyncedForTrackRef.current)) ||
      (syncTimeJump && isPlaying);

    if (!shouldSync) {
      prevSyncTimeRef.current = syncTime;
      prevIsPlayingRef.current = isPlaying;
      return;
    }

    // Seek to synchronized time
    if (player.seekTo) {
      const timeDiff = Math.abs(syncTime - currentTime);
      console.log(
        `🔄 Syncing to time: ${syncTime.toFixed(2)}s (diff: ${timeDiff.toFixed(
          2
        )}s)`
      );
      player.seekTo(syncTime, true);
      setCurrentTime(syncTime);
      lastSyncTimeRef.current = syncTime;
      hasSyncedForTrackRef.current = true;
    }

    // Update refs
    prevSyncTimeRef.current = syncTime;
    prevIsPlayingRef.current = isPlaying;
  }, [syncTime, isPlaying, player, playerReady, currentTime, isHost]);

  // Handle seek
  const handleSeek = (e) => {
    const seekTime = parseFloat(e.target.value);
    setCurrentTime(seekTime);
    isSeekingRef.current = true;

    if (player && player.seekTo) {
      player.seekTo(seekTime, true);

      // If host seeks, update Firestore immediately
      if (isHost && onTimeUpdate) {
        onTimeUpdate(seekTime);
      }
    }

    // Reset seeking flag after a short delay
    setTimeout(() => {
      isSeekingRef.current = false;
    }, 500);
  };

  // Handle volume change
  const handleVolumeChange = (e) => {
    const newVolume = parseInt(e.target.value);
    setVolume(newVolume);
    if (player && player.setVolume) {
      player.setVolume(newVolume);
    }
    if (newVolume === 0) {
      setIsMuted(true);
    } else if (isMuted) {
      setIsMuted(false);
    }
  };

  // Toggle mute
  const toggleMute = () => {
    if (player) {
      if (isMuted) {
        player.unMute();
        player.setVolume(volume);
        setIsMuted(false);
      } else {
        player.mute();
        setIsMuted(true);
      }
    }
  };

  // Format time
  const formatTime = (seconds) => {
    if (isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle play/pause button click
  const handlePlayPauseClick = () => {
    if (isPlaying) {
      onPause && onPause();
    } else {
      onPlay && onPlay();
    }
  };

  if (!videoId) {
    return (
      <div className="youtube-player">
        <div className="youtube-player-empty">
          <span>🎬 Select a YouTube video to play</span>
        </div>
      </div>
    );
  }

  return (
    <div className="youtube-player">
      {/* Video Container */}
      <div className="youtube-player-video">
        <div ref={containerRef} className="youtube-iframe-container" />
      </div>

      {/* Controls */}
      <div className="youtube-player-controls">
        {/* Track Info */}
        <div className="youtube-player-track-info">
          <p className="track-title">
            {currentTrack?.title || 'Unknown Title'}
          </p>
          <p className="track-artist">
            {currentTrack?.artist || 'Unknown Artist'}
          </p>
          {queueLength > 0 && (
            <p className="track-queue">
              {currentIndex + 1} of {queueLength}
            </p>
          )}
        </div>

        {/* Playback Controls */}
        <div className="youtube-playback-controls">
          <div className="playback-buttons">
            <button
              className="control-btn"
              onClick={onPrevious}
              disabled={!onPrevious}
            >
              ⏮
            </button>

            <button
              className="control-btn control-btn-play"
              onClick={handlePlayPauseClick}
              disabled={!playerReady}
            >
              {!playerReady ? '⏳' : isPlaying ? '⏸' : '▶'}
            </button>

            <button className="control-btn" onClick={onNext} disabled={!onNext}>
              ⏭
            </button>
          </div>

          {/* Volume */}
          <div className="volume-control">
            <button className="volume-btn" onClick={toggleMute}>
              {isMuted || volume === 0 ? '🔇' : volume < 50 ? '🔉' : '🔊'}
            </button>
            <input
              type="range"
              className="volume-slider"
              min="0"
              max="100"
              value={isMuted ? 0 : volume}
              onChange={handleVolumeChange}
            />
          </div>

          {/* Cast Button */}
          {videoId && (
            <CastButton
              mediaUrl={`https://www.youtube.com/watch?v=${videoId}`}
              mediaType="video"
              metadata={{
                title: currentTrack?.title || 'YouTube Video',
                artist: currentTrack?.artist || 'Unknown',
                thumbnail: currentTrack?.thumbnail,
              }}
            />
          )}
        </div>

        {/* Progress Bar */}
        <div className="youtube-progress-container">
          <span className="progress-time">{formatTime(currentTime)}</span>
          <input
            type="range"
            className="progress-bar"
            min="0"
            max={duration || 0}
            value={currentTime}
            onChange={handleSeek}
            disabled={!playerReady}
          />
          <span className="progress-time">{formatTime(duration)}</span>
        </div>
      </div>
    </div>
  );
}

export default YouTubePlayer;
